import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { track } from '@/lib/analytics';
import { FiArrowLeft } from 'react-icons/fi';
import { Button, Card } from '@/components';
import { apiJson, ApiError } from '@/lib/api';

function AddCheckInPage() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const [phase, setPhase] = useState<'loading' | 'ready' | 'denied'>('loading');
  const [location, setLocation] = useState<{ latitude: number; longitude: number } | null>(
    null
  );
  const [address, setAddress] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    track('Add Check In Screen');
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) {
      setPhase('denied');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setLocation({ latitude, longitude });
        setPhase('ready');
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
            { headers: { Accept: 'application/json' } }
          );
          if (res.ok) {
            const data = (await res.json()) as {
              display_name?: string;
            };
            if (data.display_name) setAddress(data.display_name);
          }
        } catch {
          /* optional reverse geocode */
        }
      },
      () => {
        setPhase('denied');
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  }, []);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!location) throw new Error('No location');
      return apiJson(getToken, '/v1/check-ins', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          latitude: location.latitude,
          longitude: location.longitude,
          address: address || undefined,
          note: note.trim() || undefined,
        }),
      });
    },
    onSuccess: async () => {
      track('Check In Created');
      await queryClient.invalidateQueries({ queryKey: ['check-ins'] });
      navigate({ to: '/app/calendar/check-ins' });
    },
    onError: (err: unknown) => {
      let message = 'Failed to save check-in. Please try again.';
      if (err instanceof ApiError && err.bodyText) {
        try {
          message =
            (JSON.parse(err.bodyText) as { detail?: string }).detail || message;
        } catch {
          /* ignore */
        }
      }
      setError(message);
    },
  });

  if (phase === 'loading') {
    return (
      <div className='min-h-screen bg-sky-light flex flex-col items-center justify-center px-6'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mb-4' />
        <p className='text-gray-600 text-center'>Getting your location…</p>
        <p className='text-xs text-gray-400 mt-4 text-center max-w-sm'>
          Your browser will ask to share your location. This documents where you checked in.
        </p>
      </div>
    );
  }

  if (phase === 'denied') {
    return (
      <div className='min-h-screen bg-sky-light px-6 py-10'>
        <Card className='space-y-4 text-center'>
          <h1 className='text-lg font-bold text-gray-900'>Location needed</h1>
          <p className='text-sm text-gray-600'>
            Allow location access in your browser settings to create a GPS check-in. This
            helps document custody exchanges with a verifiable position.
          </p>
          <Link to='/app/calendar'>
            <Button variant='secondary' fullWidth>
              Back to calendar
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-sky-light pb-10'>
      <header className='bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-lg mx-auto px-6 py-4'>
          <Link
            to='/app/calendar'
            className='inline-flex items-center gap-2 text-primary hover:underline font-semibold'
          >
            <FiArrowLeft /> Back
          </Link>
        </div>
      </header>

      <div className='max-w-lg mx-auto px-6 py-6 space-y-6'>
        <Card className='space-y-2'>
          <h1 className='text-lg font-bold text-gray-900'>Check in</h1>
          {address ? (
            <p className='text-sm text-gray-700'>{address}</p>
          ) : location ? (
            <p className='text-xs text-gray-500 font-mono'>
              {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
            </p>
          ) : null}
        </Card>

        <div>
          <label className='block text-sm font-semibold text-gray-600 mb-2'>
            Note (optional)
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder='e.g. School pickup'
            className='w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50 resize-y min-h-[88px]'
          />
        </div>

        {error ? <p className='text-sm text-red-600 text-center'>{error}</p> : null}

        <Button
          fullWidth
          onClick={() => saveMutation.mutate()}
          disabled={!location || saveMutation.isPending}
          loading={saveMutation.isPending}
        >
          Save check-in
        </Button>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/calendar/add-check-in')({
  component: AddCheckInPage,
});
